import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { AttachmentsModal } from "../attachmentsModal";
import { QuickTaskEditingPanel } from "../quickTaskEditingPanel";
import ItemStatus from "../itemStatus";
import { StyledDataGrid } from "../styledGrid";
import {
  MeetingItem,
  MeetingItemStatus,
  MeetingItemType,
} from "../../models/meetingItem";
import {
  convertToRepresentation,
  getMeetingItemFromRepresentation,
  MeetingItemRepresentation,
} from "../../models/meetingItemRepresentation";
import {
  PersonalNote,
  PersonalNoteTargetType,
  isPersonalNoteForTarget,
} from "../../models/personalNote";
import PersonalNotesModal from "./PersonalNotesModal";
import ConfirmationPanel from "../confirmationPanel";

interface MeetingNotesItemGridProps {
  items: MeetingItem[];
  meetingId: string;
  personalNotes: PersonalNote[];
  selectedItemId?: string | null;
  onDeleteItem: (itemId: string) => void | Promise<void>;
  onPersonalNotesChange: (notes: PersonalNote[]) => void;
  onUpdateAttachmentsCount: (itemId: string, count: number) => void;
  onUpdateItem: (item: MeetingItem) => Promise<boolean> | boolean;
  onSelectItem: (item: MeetingItem) => void;
}

interface WorkflowItemRow {
  id: string;
  itemId: string;
  item: MeetingItem;
  rowNumber: number;
  itemType: MeetingItemType;
  noteCount: number;
  category: string;
  content: string;
  dueDate?: Date;
  status: MeetingItemStatus;
  actor: string;
  statusLastUpdatedBy?: string;
}

const typeColors: Record<string, { bg: string; text: string }> = {
  task: { bg: "#DBEAFE", text: "#1E40AF" },
  info: { bg: "#FEF3C7", text: "#92400E" },
  decision: { bg: "#F5F0ED", text: "#5D4037" },
};

const rowHeight = 88;
const columnHeaderHeight = 50;
const maxVisibleRows = 4;

const getItemKey = (item: MeetingItem, index: number) =>
  item.id || `meeting-item-${index}`;

const getPrimaryItemType = (item: MeetingItem): MeetingItemType => {
  const primaryType = item.types?.[0];

  if (primaryType === MeetingItemType.Decision) {
    return MeetingItemType.Decision;
  }

  if (primaryType === MeetingItemType.Info) {
    return MeetingItemType.Info;
  }

  return MeetingItemType.Task;
};

const formatDueDate = (value?: Date) => {
  if (!value) {
    return "";
  }

  return dayjs(value).format("DD.MM.YYYY");
};

const toMeetingItemRepresentation = (
  item: MeetingItem,
  itemId: string,
): MeetingItemRepresentation => {
  const normalizedItem = {
    ...item,
    id: item.id || itemId,
  };
  const representation = convertToRepresentation([normalizedItem])[0];

  return {
    ...(representation as MeetingItemRepresentation),
    id: normalizedItem.id,
    overdue: false,
    data: normalizedItem,
  };
};

const MeetingNotesItemGrid = memo(
  ({
    items,
    meetingId,
    personalNotes,
    selectedItemId,
    onDeleteItem,
    onPersonalNotesChange,
    onUpdateAttachmentsCount,
    onUpdateItem,
    onSelectItem,
  }: MeetingNotesItemGridProps) => {
    const { t } = useTranslation();
    const safePersonalNotes = useMemo(
      () => (Array.isArray(personalNotes) ? personalNotes : []),
      [personalNotes],
    );
    const [actionMenuAnchor, setActionMenuAnchor] =
      useState<HTMLElement | null>(null);
    const [actionMenuItemId, setActionMenuItemId] = useState<string | null>(
      null,
    );
    const [detailsItemId, setDetailsItemId] = useState<string | null>(null);
    const [notesDialogItemId, setNotesDialogItemId] = useState<string | null>(
      null,
    );
    const [attachmentsItemId, setAttachmentsItemId] = useState<string | null>(
      null,
    );
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<MeetingItem | null>(null);

    const noteCountsByItemId = useMemo(
      () =>
        safePersonalNotes.reduce<Record<string, number>>((counts, note) => {
          if (
            note.targetType !== PersonalNoteTargetType.MeetingItem ||
            !note.targetId
          ) {
            return counts;
          }

          counts[note.targetId] = (counts[note.targetId] || 0) + 1;
          return counts;
        }, {}),
      [safePersonalNotes],
    );

    const gridComputedHeight =
      columnHeaderHeight +
      rowHeight * Math.min(items.length, maxVisibleRows) +
      2;

    const gridRows: WorkflowItemRow[] = useMemo(
      () =>
        items.map((item, index) => {
          const itemId = getItemKey(item, index);

          return {
            id: itemId,
            itemId,
            item,
            rowNumber: index + 1,
            itemType: getPrimaryItemType(item),
            noteCount: noteCountsByItemId[itemId] || 0,
            category: item.category || "",
            content: item.description || item.content || "",
            dueDate: item.dueDate,
            status: item.status,
            actor: item.actor || "",
            statusLastUpdatedBy: item.statusLastUpdatedBy,
          };
        }),
      [items, noteCountsByItemId],
    );

    const allItemRepresentations: MeetingItemRepresentation[] = useMemo(
      () =>
        items.map((item, index) =>
          toMeetingItemRepresentation(item, getItemKey(item, index)),
        ),
      [items],
    );

    const getItemById = useCallback(
      (itemId: string | null) =>
        itemId
          ? items.find((item, index) => getItemKey(item, index) === itemId) ||
          null
          : null,
      [items],
    );

    const actionMenuItem = getItemById(actionMenuItemId);
    const detailsItemSource = getItemById(detailsItemId);
    const notesItem = getItemById(notesDialogItemId);
    const attachmentsItem = getItemById(attachmentsItemId);
    const detailsItem = useMemo(
      () =>
        detailsItemId && detailsItemSource
          ? toMeetingItemRepresentation(detailsItemSource, detailsItemId)
          : null,
      [detailsItemId, detailsItemSource],
    );

    useEffect(() => {
      if (
        actionMenuItemId &&
        !items.some(
          (item, index) => getItemKey(item, index) === actionMenuItemId,
        )
      ) {
        setActionMenuAnchor(null);
        setActionMenuItemId(null);
      }
    }, [actionMenuItemId, items]);

    useEffect(() => {
      if (
        detailsItemId &&
        !items.some((item, index) => getItemKey(item, index) === detailsItemId)
      ) {
        setDetailsItemId(null);
      }
    }, [detailsItemId, items]);

    useEffect(() => {
      if (
        notesDialogItemId &&
        !items.some(
          (item, index) => getItemKey(item, index) === notesDialogItemId,
        )
      ) {
        setNotesDialogItemId(null);
      }
    }, [notesDialogItemId, items]);

    useEffect(() => {
      if (
        attachmentsItemId &&
        !items.some(
          (item, index) => getItemKey(item, index) === attachmentsItemId,
        )
      ) {
        setAttachmentsItemId(null);
      }
    }, [attachmentsItemId, items]);

    const handleOpenActionMenu = useCallback(
      (event: MouseEvent<HTMLElement>, itemId: string) => {
        event.stopPropagation();
        setActionMenuAnchor(event.currentTarget);
        setActionMenuItemId(itemId);
      },
      [],
    );

    const handleCloseActionMenu = useCallback(() => {
      setActionMenuAnchor(null);
      setActionMenuItemId(null);
    }, []);

    const handleOpenNotesDialog = useCallback((itemId: string) => {
      setNotesDialogItemId(itemId);
    }, []);

    const handleOpenDetails = useCallback(
      (itemId: string) => {
        const item = getItemById(itemId);
        if (item) {
          onSelectItem(item);
        }
        setDetailsItemId(itemId);
      },
      [getItemById, onSelectItem],
    );

    const handleOpenDetailsFromMenu = useCallback(() => {
      if (!actionMenuItemId) {
        return;
      }

      handleCloseActionMenu();
      handleOpenDetails(actionMenuItemId);
    }, [actionMenuItemId, handleCloseActionMenu, handleOpenDetails]);

    const handleOpenNotesFromMenu = useCallback(() => {
      if (!actionMenuItemId) {
        return;
      }

      handleCloseActionMenu();
      handleOpenNotesDialog(actionMenuItemId);
    }, [actionMenuItemId, handleCloseActionMenu, handleOpenNotesDialog]);

    const handleDialogNotesChange = useCallback(
      (updatedTargetNotes: PersonalNote[]) => {
        if (!notesDialogItemId) {
          return;
        }

        const remainingNotes = safePersonalNotes.filter(
          (note) =>
            !isPersonalNoteForTarget(
              note,
              PersonalNoteTargetType.MeetingItem,
              notesDialogItemId,
            ),
        );

        onPersonalNotesChange([
          ...remainingNotes,
          ...(Array.isArray(updatedTargetNotes) ? updatedTargetNotes : []),
        ]);
      },
      [notesDialogItemId, onPersonalNotesChange, safePersonalNotes],
    );

    const handleOpenAttachments = useCallback((itemId: string) => {
      setAttachmentsItemId(itemId);
    }, []);

    const handleOpenAttachmentsFromMenu = useCallback(() => {
      if (!actionMenuItemId) {
        return;
      }

      handleCloseActionMenu();
      handleOpenAttachments(actionMenuItemId);
    }, [actionMenuItemId, handleCloseActionMenu, handleOpenAttachments]);

    const showDeleteConfirmation = useCallback((item: MeetingItem) => {
      setItemToDelete(item);
      setDeleteConfirmOpen(true);
    }, []);

    const hideDeleteConfirmation = useCallback(() => {
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }, []);

    const confirmDeleteItem = useCallback(async () => {
      if (!itemToDelete?.id) return;
      await onDeleteItem(itemToDelete.id);
      hideDeleteConfirmation();
    }, [itemToDelete, onDeleteItem, hideDeleteConfirmation]);

    const handleDeleteFromMenu = useCallback(() => {
      const itemId = actionMenuItemId;
      handleCloseActionMenu();
      if (!itemId) {
        return;
      }

      const item = items.find(
        (i) => i.id === itemId || getItemKey(i, items.indexOf(i)) === itemId,
      );
      if (item) {
        showDeleteConfirmation(item);
      }
    }, [
      actionMenuItemId,
      handleCloseActionMenu,
      items,
      showDeleteConfirmation,
    ]);

    const handleUpdateDetailsItem = useCallback(
      async (updatedRepresentation: MeetingItemRepresentation) => {
        const updatedItem = getMeetingItemFromRepresentation(
          updatedRepresentation,
        );
        if (!updatedItem) {
          return;
        }

        updatedItem.id = updatedItem.id || updatedRepresentation.id;
        await onUpdateItem(updatedItem);
      },
      [onUpdateItem],
    );
    const getItemTypeLabel = useCallback(
      (itemType?: MeetingItemType | string) => {
        if (itemType === MeetingItemType.Decision || itemType === "decision") {
          return t("meetingNotes.common.itemTypes.decision");
        }

        if (itemType === MeetingItemType.Info || itemType === "info") {
          return t("meetingNotes.common.itemTypes.info");
        }

        return t("meetingNotes.common.itemTypes.task");
      },
      [t],
    );

    const columns = useMemo<GridColDef<WorkflowItemRow>[]>(
      () => [
        {
          field: "rowNumber",
          headerName: t("meetingNotes.itemGrid.headers.id"),
          width: 35,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (
            params: GridRenderCellParams<WorkflowItemRow, number>,
          ) => (
            <Typography sx={{ fontSize: "0.875rem", color: "#AAB0C5" }}>
              #{String(params.value || 0).padStart(3, "0")}
            </Typography>
          ),
        },
        {
          field: "itemType",
          headerName: t("meetingNotes.itemGrid.headers.type"),
          width: 128,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (
            params: GridRenderCellParams<WorkflowItemRow, MeetingItemType>,
          ) => {
            const typeColor =
              typeColors[params.value || MeetingItemType.Task] ||
              typeColors.task;

            return (
              <Chip
                icon={
                  params.value === MeetingItemType.Decision ? (
                    <GavelOutlinedIcon sx={{ fontSize: 16 }} />
                  ) : params.value === MeetingItemType.Info ? (
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <TaskAltOutlinedIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={
                  getItemTypeLabel(params.value)
                }
                size="small"
                sx={[{
                  height: 30,
                  fontSize: "0.84rem",
                  fontWeight: 500,
                  borderRadius: "999px",
                  "& .MuiChip-label": { px: 1 },

                  "& .MuiChip-icon": {
                    fontSize: 16
                  }
                }, params.value === MeetingItemType.Task ? {
                  backgroundColor: "#EAF8EF"
                } : {
                  backgroundColor: typeColor.bg
                }, params.value === MeetingItemType.Task ? {
                  color: "#169C4A"
                } : {
                  color: typeColor.text
                }, params.value === MeetingItemType.Task ? {
                  "& .MuiChip-icon": {
                    color: "#169C4A"
                  }
                } : {
                  "& .MuiChip-icon": {
                    color: typeColor.text
                  }
                }]}
              />
            );
          },
        },
        {
          field: "category",
          headerName: t("meetingNotes.itemGrid.headers.category"),
          minWidth: 130,
          flex: 0.95,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (
            params: GridRenderCellParams<WorkflowItemRow, string>,
          ) => (
            <Tooltip title={params.value || ""} placement="top" arrow>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    fontSize: "0.9375rem",
                    color: params.value ? "#52627B" : "#AAB0C5",
                  }}
                >
                  {params.value || ""}
                </Typography>
              </Box>
            </Tooltip>
          ),
        },
        {
          field: "content",
          headerName: t("meetingNotes.itemGrid.headers.item"),
          minWidth: 230,
          flex: 1.8,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (
            params: GridRenderCellParams<WorkflowItemRow, string>,
          ) => (
            <Box sx={{ minWidth: 0 }}>
              <Tooltip title={params.value} placement="top" arrow>
                <Typography
                  sx={{
                    fontSize: "0.9375rem",
                    color: "#1A2048",
                    lineHeight: 1.4,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {params.value}
                </Typography>
              </Tooltip>
              {params.row.noteCount > 0 ? (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: "0.75rem",
                    color: "#7B869C",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 13 }} />
                  {t("meetingNotes.itemGrid.noteCount", {
                    count: params.row.noteCount,
                  })}
                </Typography>
              ) : null}
            </Box>
          ),
        },
        {
          field: "dueDate",
          headerName: t("meetingNotes.itemGrid.headers.dueDate"),
          minWidth: 120,
          flex: 0.85,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (
            params: GridRenderCellParams<WorkflowItemRow, Date | undefined>,
          ) => {
            const isTask = params.row.itemType === MeetingItemType.Task;
            if (!isTask) {
              return (
                <Typography
                  sx={{
                    fontSize: "0.9375rem",
                    color: "#C0C6D4",
                    fontStyle: "italic",
                  }}
                >
                  {t("meetingNotes.itemGrid.notApplicable")}
                </Typography>
              );
            }
            return (
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  color: params.value ? "#7B869C" : "#C0C6D4",
                  fontStyle: "italic",
                }}
              >
                {params.value
                  ? formatDueDate(params.value)
                  : t("meetingNotes.itemGrid.noDueDate")}
              </Typography>
            );
          },
        },
        {
          field: "owner",
          headerName: t("meetingNotes.itemGrid.headers.owner"),
          width: 84,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams<WorkflowItemRow>) => {
            if (params.row.itemType !== MeetingItemType.Task) {
              return (
                <Typography
                  sx={{
                    fontSize: "0.9375rem",
                    color: "#C0C6D4",
                    fontStyle: "italic",
                  }}
                >
                  {t("meetingNotes.itemGrid.notApplicable")}
                </Typography>
              );
            }

            return (
              <Tooltip
                title={params.row.actor || t("meetingNotes.itemGrid.unassigned")}
                placement="top"
                arrow
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#F3F4F6",
                    color: "#56627A",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                >
                  {params.row.actor.trim()?.charAt(0).toUpperCase() || "?"}
                </Box>
              </Tooltip>
            );
          },
        },
        {
          field: "status",
          headerName: t("meetingNotes.itemGrid.headers.status"),
          width: 180,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (
            params: GridRenderCellParams<WorkflowItemRow, MeetingItemStatus>,
          ) => {
            const isTask = params.row.itemType === MeetingItemType.Task;

            if (!isTask) {
              return (
                <Typography
                  sx={{
                    fontSize: "0.9375rem",
                    color: "#C0C6D4",
                    fontStyle: "italic",
                  }}
                >
                  {t("meetingNotes.itemGrid.notApplicable")}
                </Typography>
              );
            }

            return (
              <Box onClick={(event) => event.stopPropagation()}>
                <ItemStatus
                  status={params.value || MeetingItemStatus.NotStarted}
                  lastModifiedBy={params.row.statusLastUpdatedBy}
                  onChange={(newStatus) =>
                    void onUpdateItem({
                      ...params.row.item,
                      id: params.row.item.id || params.row.itemId,
                      status: newStatus,
                      updatedDate: new Date(),
                    })
                  }
                />
              </Box>
            );
          },
        },
        {
          field: "actions",
          headerName: t("meetingNotes.itemGrid.headers.actions"),
          width: 89,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams<WorkflowItemRow>) => (
            <IconButton
              size="small"
              onClick={(event) =>
                handleOpenActionMenu(event, params.row.itemId)
              }
              sx={{
                color: "#64748B",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
              }}
            >
              <MoreVertRoundedIcon />
            </IconButton>
          ),
        },
      ],
      [getItemTypeLabel, handleOpenActionMenu, onUpdateItem, t],
    );

    const handleGridCellClick = useCallback(
      (params: GridCellParams<WorkflowItemRow>) => {
        if (params.field === "status" || params.field === "actions") {
          return;
        }

        onSelectItem(params.row.item);
      },
      [onSelectItem],
    );

    const getRowClassName = useCallback(
      (params: GridRowClassNameParams<WorkflowItemRow>) =>
        params.row.itemId === selectedItemId
          ? "grid-theme workflow-row-selected"
          : "grid-theme",
      [selectedItemId],
    );

    const gridSx = useMemo(
      () => ({
        height: "100%",
        width: "100%",
        border: 0,
        borderRadius: 0,
        backgroundColor: "#FFFFFF",
        "& .MuiDataGrid-columnHeaders": {
          borderBottom: "2px solid #242C63",
          backgroundColor: "#FFFFFF",
        },
        "& .MuiDataGrid-main": {
          backgroundColor: "#FFFFFF",
        },
        "& .MuiDataGrid-columnSeparator": {
          display: "none",
        },
        "& .MuiDataGrid-columnHeader": {
          px: 1.5,
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          color: "#5C6B85",
          fontSize: "0.75rem",
          fontWeight: 700,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        },
        "& .MuiDataGrid-row": {
          cursor: "pointer",
          maxHeight: "88px !important",
          minHeight: "88px !important",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "#fafafa",
        },
        "& .MuiDataGrid-row.workflow-row-selected": {
          backgroundColor: "#fafafa",
        },
        "& .MuiDataGrid-row.workflow-row-selected:hover": {
          backgroundColor: "#fafafa",
        },
        "& .MuiDataGrid-cell": {
          borderBottom: "1px solid #E5E7EB",
          px: 1.5,
          color: "#1A2048",
          display: "flex",
          alignItems: "center",
        },
        "& .MuiDataGrid-cell[data-field='actions']": {
          justifyContent: "center",
        },
        "& .MuiDataGrid-virtualScroller": {
          overflowX: "hidden",
          overflowY: "auto",
        },
        "& .MuiDataGrid-scrollbar--horizontal": {
          display: "none",
        },
        "& .MuiDataGrid-footerContainer": {
          display: "none",
        },
      }),
      [],
    );

    return (
      <>
        {items.length === 0 ? (
          <Box
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "16px",
              backgroundColor: "#FFFFFF",
              py: 6,
              textAlign: "center",
              color: "#6B7280",
            }}
          >
            <Typography>
              {t("meetingNotes.itemGrid.empty")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              height: `${gridComputedHeight}px`,
              transition: "height 180ms ease",
              width: "100%",
              border: "1px solid #E5E7EB",
              borderRadius: "16px",
              backgroundColor: "#FFFFFF",
              overflow: "hidden",
            }}
          >
            <StyledDataGrid
              rows={gridRows}
              columns={columns}
              hideFooter
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              disableRowSelectionOnClick
              rowHeight={rowHeight}
              columnHeaderHeight={columnHeaderHeight}
              onCellClick={handleGridCellClick}
              getRowClassName={getRowClassName}
              sx={gridSx}
            />
          </Box>
        )}

        <PersonalNotesModal
          open={Boolean(notesItem)}
          onClose={() => setNotesDialogItemId(null)}
          targetType={PersonalNoteTargetType.MeetingItem}
          targetId={notesDialogItemId}
          targetItem={notesItem}
          onNotesChange={handleDialogNotesChange}
        />

        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleCloseActionMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                minWidth: 196,
                border: "1px solid #E5E7EB",
                boxShadow: "0px 16px 34px rgba(15, 23, 42, 0.12)",
                mt: 0.75,
                overflow: "hidden",
              },
            },
          }}
        >
          <MenuItem
            onClick={handleOpenDetailsFromMenu}
            sx={{ gap: 1.5, py: 1.25 }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "#7B869C" }} />
            <Typography sx={{ fontSize: "0.95rem", color: "#1A2048" }}>
              {t("meetingNotes.itemGrid.menu.viewDetails")}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={handleOpenNotesFromMenu}
            sx={{ gap: 1.5, py: 1.25 }}
          >
            <VpnKeyOutlinedIcon sx={{ fontSize: 18, color: "#7B869C" }} />
            <Typography sx={{ fontSize: "0.95rem", color: "#1A2048" }}>
              {t("meetingNotes.itemGrid.menu.personalNotes")}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={handleOpenAttachmentsFromMenu}
            disabled={!actionMenuItem?.id || !meetingId}
            sx={{ gap: 1.5, py: 1.25 }}
          >
            <AttachFileOutlinedIcon sx={{ fontSize: 18, color: "#7B869C" }} />
            <Typography sx={{ fontSize: "0.95rem", color: "#1A2048" }}>
              {t("meetingNotes.itemGrid.menu.attachments")}
              {actionMenuItem?.attachments
                ? ` (${actionMenuItem.attachments})`
                : ""}
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleDeleteFromMenu} sx={{ gap: 1.5, py: 1.25 }}>
            <DeleteOutlineOutlinedIcon
              sx={{ fontSize: 18, color: "#E53935" }}
            />
            <Typography sx={{ fontSize: "0.95rem", color: "#E53935" }}>
              {t("button.delete")}
            </Typography>
          </MenuItem>
        </Menu>

        {detailsItem ? (
          <QuickTaskEditingPanel
            meetingItem={detailsItem}
            open={Boolean(detailsItem)}
            allowMetadataEditing={true}
            allowAttachmentsEditing={true}
            showPersonalNotesButton={false}
            items={allItemRepresentations}
            onNavigateTo={(item) => setDetailsItemId(item.id)}
            onClose={() => setDetailsItemId(null)}
            onUpdate={handleUpdateDetailsItem}
            onAttachmentsCountChanged={(count, _meetingId, itemId) => {
              onUpdateAttachmentsCount(itemId || detailsItem.id, count);
            }}
          />
        ) : null}

        <AttachmentsModal
          open={Boolean(attachmentsItem?.id && attachmentsItemId)}
          height="70vh"
          allowModifications={true}
          meetingId={meetingId}
          itemId={attachmentsItem?.id}
          onClose={() => setAttachmentsItemId(null)}
          uploadPrefix={
            attachmentsItem?.id
              ? `items/meetings/${meetingId}/items/${attachmentsItem.id}/`
              : undefined
          }
          onAttachmentsCountChanged={(count, _meetingId, itemId) => {
            if (!itemId && !attachmentsItemId) {
              return;
            }

            onUpdateAttachmentsCount(itemId || attachmentsItemId || "", count);
          }}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmationPanel
          showModal={deleteConfirmOpen}
          hideModal={hideDeleteConfirmation}
          confirmModal={confirmDeleteItem}
          entry={itemToDelete}
          type="item"
          action="delete"
        />
      </>
    );
  },
);

MeetingNotesItemGrid.displayName = "MeetingNotesItemGrid";

export default MeetingNotesItemGrid;
